import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Webhook,
  Plus,
  MoreHorizontal,
  Trash2,
  Edit,
  Play,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
  Copy,
  ExternalLink,
  LayoutGrid,
  List,
  Calendar,
  Lock,
  Sparkles,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { webhooksApi } from "@/services/api";
import { useAuthStore } from "@/store/auth.store";
import { formatDateShort, formatRelativeTime } from "@/utils/dateFormat";
import { multipleClassesName } from "@/utils/transform";

// Webhook events - using hook for translations
const useWebhookEvents = () => {
  const { t } = useTranslation();
  return [
    {
      value: "envelope.created",
      label: t("webhooks.events.envelopeCreated"),
      desc: t("webhooks.events.envelopeCreatedDesc"),
    },
    {
      value: "envelope.sent",
      label: t("webhooks.events.envelopeSent"),
      desc: t("webhooks.events.envelopeSentDesc"),
    },
    {
      value: "envelope.viewed",
      label: t("webhooks.events.envelopeViewed"),
      desc: t("webhooks.events.envelopeViewedDesc"),
    },
    {
      value: "envelope.signed",
      label: t("webhooks.events.envelopeSigned"),
      desc: t("webhooks.events.envelopeSignedDesc"),
    },
    {
      value: "envelope.completed",
      label: t("webhooks.events.envelopeCompleted"),
      desc: t("webhooks.events.envelopeCompletedDesc"),
    },
    {
      value: "envelope.declined",
      label: t("webhooks.events.envelopeDeclined"),
      desc: t("webhooks.events.envelopeDeclinedDesc"),
    },
    {
      value: "envelope.voided",
      label: t("webhooks.events.envelopeVoided"),
      desc: t("webhooks.events.envelopeVoidedDesc"),
    },
    {
      value: "envelope.expired",
      label: t("webhooks.events.envelopeExpired"),
      desc: t("webhooks.events.envelopeExpiredDesc"),
    },
    {
      value: "recipient.signed",
      label: t("webhooks.events.recipientSigned"),
      desc: t("webhooks.events.recipientSignedDesc"),
    },
    {
      value: "recipient.declined",
      label: t("webhooks.events.recipientDeclined"),
      desc: t("webhooks.events.recipientDeclinedDesc"),
    },
  ];
};

// Webhook card component for grid view
const WebhookCard = ({ webhook, onEdit, onDelete, onTest, isTestLoading }) => {
  const { t } = useTranslation();

  const successRate =
    webhook.totalDeliveries > 0
      ? Math.round(
          (webhook.successfulDeliveries / webhook.totalDeliveries) * 100,
        )
      : 100;

  return (
    <Card className="group hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="h-16 bg-primary/10 flex items-center justify-center relative">
        <Webhook
          className={`h-8 w-8 ${
            webhook.isActive ? "text-primary" : "text-muted-foreground"
          }`}
        />

        {/* Actions dropdown */}
        <div className="absolute top-2 right-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(webhook)}>
                <Edit className="mr-2 h-4 w-4" />
                {t("common.edit")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onTest(webhook)}
                disabled={isTestLoading}
              >
                {isTestLoading ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Play className="mr-2 h-4 w-4" />
                )}
                {t("webhooks.test")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => window.open(webhook.url, "_blank")}
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                {t("webhooks.openUrl")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onDelete(webhook)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t("common.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Status badge */}
        <div className="absolute bottom-2 left-2">
          {webhook.isActive ? (
            <Badge
              variant="outline"
              className="bg-emerald-500/10 text-emerald-600 border-emerald-200"
            >
              {t("webhooks.active")}
            </Badge>
          ) : (
            <Badge variant="secondary">{t("webhooks.inactive")}</Badge>
          )}
        </div>
      </div>

      {/* Content */}
      <CardContent className="p-4 dark:bg-background">
        <div className="mb-2">
          <h3 className="font-medium truncate">
            {webhook.name || t("webhooks.unnamedWebhook")}
          </h3>
          <p className="text-xs text-muted-foreground truncate">
            {webhook.url}
          </p>
        </div>

        {/* Events */}
        <div className="flex flex-wrap gap-1 mb-3">
          {webhook.events?.slice(0, 3).map((event) => (
            <Badge
              key={event}
              variant="secondary"
              className="text-xs font-normal"
            >
              {event.split(".")[1]}
            </Badge>
          ))}
          {webhook.events?.length > 3 && (
            <Badge variant="outline" className="text-xs font-normal">
              +{webhook.events.length - 3}
            </Badge>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between text-sm mb-3">
          <span className="text-muted-foreground">
            {t("webhooks.successRate")}
          </span>
          <span
            className={multipleClassesName([
              successRate >= 90 ? "text-emerald-600 font-medium" : "",
              successRate >= 70
                ? "text-yellow-600 font-medium"
                : "text-red-600 font-medium",
            ])}
          >
            {successRate}%
            <span className="text-muted-foreground font-normal ml-1">
              ({webhook.totalDeliveries || 0})
            </span>
          </span>
        </div>

        {/* Footer */}
        <div className="pt-3 border-t text-xs text-muted-foreground flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {webhook.lastDeliveryAt
            ? `Last: ${formatRelativeTime(webhook.lastDeliveryAt)}`
            : t("webhooks.noDeliveriesYet")}
        </div>
      </CardContent>
    </Card>
  );
};

// Webhook table row component
const WebhookRow = ({ webhook, onEdit, onDelete, onTest, isTestLoading }) => {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const [showSecret, setShowSecret] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Fetch deliveries when expanded
  const { data: deliveries } = useQuery({
    queryKey: ["webhook-deliveries", webhook.id],
    queryFn: () =>
      webhooksApi
        .getDeliveries(webhook.id, { limit: 5 })
        .then((res) => res.data?.data || []),
    enabled: expanded,
  });

  const copySecret = () => {
    if (webhook.secret) {
      navigator.clipboard.writeText(webhook.secret);
      toast.success(t("webhooks.secretCopied"));
    }
  };

  const successRate =
    webhook.totalDeliveries > 0
      ? Math.round(
          (webhook.successfulDeliveries / webhook.totalDeliveries) * 100,
        )
      : 100;

  const maskedSecret = webhook.secret
    ? `${webhook.secret.slice(0, 8)}${"•".repeat(16)}${webhook.secret.slice(
        -4,
      )}`
    : null;

  return (
    <>
      <TableRow className="group">
        <TableCell>
          <div className="flex items-center gap-3">
            <div
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                webhook.isActive ? "bg-primary/10" : "bg-muted"
              }`}
            >
              <Webhook
                className={`h-4 w-4 ${
                  webhook.isActive ? "text-primary" : "text-muted-foreground"
                }`}
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-medium">
                {webhook.name || t("webhooks.unnamedWebhook")}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {webhook.url}
              </div>
            </div>
          </div>
        </TableCell>
        <TableCell>
          <div className="flex flex-wrap gap-1">
            {webhook.events?.slice(0, 2).map((event) => (
              <Badge
                key={event}
                variant="secondary"
                className="text-xs font-normal"
              >
                {event.split(".")[1]}
              </Badge>
            ))}
            {webhook.events?.length > 2 && (
              <Badge variant="outline" className="text-xs font-normal">
                +{webhook.events.length - 2}
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell>
          {webhook.isActive ? (
            <Badge
              variant="outline"
              className="bg-emerald-500/10 text-emerald-600 border-emerald-200"
            >
              {t("webhooks.active")}
            </Badge>
          ) : (
            <Badge variant="secondary">{t("webhooks.inactive")}</Badge>
          )}
        </TableCell>
        <TableCell>
          <div className="text-sm">
            <span
              className={multipleClassesName([
                successRate >= 90 ? "text-emerald-600" : "",
                successRate >= 70 ? "text-yellow-600" : "text-red-600",
              ])}
            >
              {successRate}%
            </span>
            <span className="text-muted-foreground ml-1">
              ({webhook.totalDeliveries || 0})
            </span>
          </div>
        </TableCell>
        <TableCell className="text-muted-foreground text-sm">
          {webhook.lastDeliveryAt
            ? formatRelativeTime(webhook.lastDeliveryAt)
            : "-"}
        </TableCell>
        <TableCell>
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setExpanded(!expanded)}
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onTest(webhook)}
              disabled={isTestLoading}
            >
              {isTestLoading ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Play className="h-4 w-4" />
              )}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(webhook)}>
                  <Edit className="mr-2 h-4 w-4" />
                  {t("common.edit")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => window.open(webhook.url, "_blank")}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  {t("webhooks.openUrl")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onDelete(webhook)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("common.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className="bg-muted/30 hover:bg-muted/30">
          <TableCell colSpan={6} className="p-4">
            <div className="space-y-4">
              {/* Webhook Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">
                    {t("webhooks.endpointUrl")}
                  </Label>
                  <p className="text-sm font-mono break-all">{webhook.url}</p>
                </div>
                {webhook.secret && (
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      {t("webhooks.signingSecret")}
                    </Label>
                    <div className="flex items-center gap-2">
                      <code className="text-xs font-mono">
                        {showSecret ? webhook.secret : maskedSecret}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => setShowSecret(!showSecret)}
                      >
                        {showSecret ? (
                          <EyeOff className="h-3 w-3" />
                        ) : (
                          <Eye className="h-3 w-3" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={copySecret}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Events */}
              <div>
                <Label className="text-xs text-muted-foreground">
                  {t("webhooks.subscribedEvents")}
                </Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {webhook.events?.map((event) => (
                    <Badge key={event} variant="outline" className="text-xs">
                      {event}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Recent Deliveries */}
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">
                  {t("webhooks.recentDeliveries")}
                </Label>
                {deliveries?.length > 0 ? (
                  <div className="space-y-2">
                    {deliveries.map((delivery) => (
                      <div
                        key={delivery.id}
                        className="flex items-center justify-between p-2 bg-background rounded-lg border"
                      >
                        <div className="flex items-center gap-3">
                          {delivery.success ||
                          (delivery.statusCode >= 200 &&
                            delivery.statusCode < 300) ? (
                            <CheckCircle className="h-4 w-4 text-emerald-500" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-red-500" />
                          )}
                          <div>
                            <p className="text-sm font-medium">
                              {delivery.event}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatDateShort(delivery.createdAt, user)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge
                            variant={
                              delivery.statusCode >= 200 &&
                              delivery.statusCode < 300
                                ? "outline"
                                : "destructive"
                            }
                            className="text-xs"
                          >
                            {delivery.statusCode}
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1">
                            {delivery.responseTime}ms
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t("webhooks.noDeliveries")}
                  </p>
                )}
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

// Loading skeleton
const WebhooksSkeleton = () => (
  <div className="space-y-6">
    <div className="flex justify-between">
      <div className="space-y-2">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-64" />
      </div>
      <Skeleton className="h-10 w-32" />
    </div>
    <Card>
      <CardContent className="p-0">
        <div className="p-4 space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      </CardContent>
    </Card>
  </div>
);

// Empty state
const EmptyState = ({ onCreateClick }) => {
  const { t } = useTranslation();

  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
          <Webhook className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold mb-1">
          {t("webhooks.noWebhooks")}
        </h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-sm">
          {t("webhooks.noWebhooksDesc")}
        </p>
        <Button onClick={onCreateClick}>
          <Plus className="mr-2 h-4 w-4" />
          {t("webhooks.add")}
        </Button>
      </CardContent>
    </Card>
  );
};

// ============================================
// UpgradeRequired Component
// ============================================
const UpgradeRequired = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold font-heading tracking-tight">
          {t("webhooks.title", "Webhooks")}
        </h1>
        <p className="text-muted-foreground">
          {t(
            "webhooks.subtitle",
            "Receive real-time notifications for document events",
          )}
        </p>
      </div>

      {/* Single Card with divider */}
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 mb-6">
            <Lock className="h-10 w-10 text-primary" />
          </div>
          <h2 className="text-2xl font-semibold mb-2">
            {t("upgrade.featureNotAvailable", "Feature Not Available")}
          </h2>
          <p className="text-muted-foreground mb-6 max-w-md">
            {t(
              "upgrade.webhooksUpgradeDesc",
              "Webhooks allow you to receive real-time HTTP notifications when events like document signing or completion occur.",
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={() => navigate("/settings/billing")}>
              <Sparkles className="h-4 w-4 mr-2" />
              {t("upgrade.upgradePlan", "Upgrade Plan")}
            </Button>
            <Button variant="outline" onClick={() => navigate("/envelopes")}>
              {t("common.backToEnvelopes", "Back to Envelopes")}
            </Button>
          </div>

          {/* Feature highlights */}
          <div className="mt-8 pt-8 border-t w-full max-w-lg">
            <div className="w-full flex items-center justify-center gap-2 mb-4">
              <Webhook className="h-3.5 w-3.5 text-primary" />
              <h3 className="text-sm font-medium text-muted-foreground">
                {t("webhooks.withWebhooksYouCan", "With Webhooks you can:")}
              </h3>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1 flex items-center justify-center flex-col">
              <li>
                {t(
                  "webhooks.feature1",
                  "Receive real-time notifications for document events",
                )}
              </li>
              <li>
                {t(
                  "webhooks.feature2",
                  "Integrate with your existing systems and workflows",
                )}
              </li>
              <li>
                {t(
                  "webhooks.feature3",
                  "Automate actions based on signing status changes",
                )}
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const Webhooks = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Get user subscription to check feature access
  const user = useAuthStore((state) => state.user);

  // Check if user has webhooks feature - must be explicitly true
  const hasWebhooksFeature = user?.subscription?.features?.webhooks === true;

  // View mode state
  const [viewMode, setViewMode] = useState("grid");

  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [testingWebhookId, setTestingWebhookId] = useState(null);

  // Fetch webhooks (only if feature enabled)
  const { data: webhooks, isLoading } = useQuery({
    queryKey: ["webhooks"],
    queryFn: () => webhooksApi.list().then((res) => res.data?.data || []),
    enabled: hasWebhooksFeature, // Don't fetch if feature is disabled
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => webhooksApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["webhooks"]);
      toast.success(t("webhooks.deleted"));
      setShowDeleteDialog(false);
      setSelectedWebhook(null);
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || t("errors.generic"));
    },
  });

  // Test mutation
  const testMutation = useMutation({
    mutationFn: (id) => {
      setTestingWebhookId(id);
      return webhooksApi.test(id);
    },
    onSuccess: () => {
      toast.success(t("webhooks.testSent"));
      queryClient.invalidateQueries(["webhooks"]);
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || t("webhooks.testFailed"));
    },
    onSettled: () => {
      setTestingWebhookId(null);
    },
  });

  const openEditDialog = (webhook) => {
    setEditingWebhook(webhook);
    setShowCreateDialog(true);
  };

  const openDeleteDialog = (webhook) => {
    setSelectedWebhook(webhook);
    setShowDeleteDialog(true);
  };

  const handleCloseCreateDialog = () => {
    setShowCreateDialog(false);
    setEditingWebhook(null);
  };

  const renderContent = () => {
    if (webhooks?.length === 0) {
      return <EmptyState onCreateClick={() => setShowCreateDialog(true)} />;
    }
    return viewMode === "grid" ? (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {webhooks?.map((webhook) => (
          <WebhookCard
            key={webhook.id}
            webhook={webhook}
            onEdit={openEditDialog}
            onDelete={openDeleteDialog}
            onTest={() => testMutation.mutate(webhook.id)}
            isTestLoading={testingWebhookId === webhook.id}
          />
        ))}
      </div>
    ) : (
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-75">{t("webhooks.webhook")}</TableHead>
                <TableHead className="w-37.5">{t("webhooks.events.title")}</TableHead>
                <TableHead className="w-25">{t("webhooks.status")}</TableHead>
                <TableHead className="w-30">
                  {t("webhooks.successRate")}
                </TableHead>
                <TableHead className="w-30">
                  {t("webhooks.lastDelivery")}
                </TableHead>
                <TableHead className="w-30 text-right">
                  {t("common.actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {webhooks?.map((webhook) => (
                <WebhookRow
                  key={webhook.id}
                  webhook={webhook}
                  onEdit={openEditDialog}
                  onDelete={openDeleteDialog}
                  onTest={() => testMutation.mutate(webhook.id)}
                  isTestLoading={testingWebhookId === webhook.id}
                />
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  // ============================================
  // Feature Check - Show upgrade message if webhooks not available
  // ============================================

  if (!hasWebhooksFeature) {
    return <UpgradeRequired />;
  }

  if (isLoading) {
    return <WebhooksSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("webhooks.title")}
          </h1>
          <p className="text-muted-foreground">{t("webhooks.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex items-center border rounded-lg p-1">
            <Button
              variant={viewMode === "grid" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setViewMode("grid")}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setViewMode("list")}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t("webhooks.add")}
          </Button>
        </div>
      </div>

      {/* Info card */}
      <Card className="bg-muted/50">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground">
            {t("webhooks.infoText")}
          </p>
        </CardContent>
      </Card>

      {/* Webhooks list */}
      {renderContent()}

      {/* Create/Edit Dialog */}
      <WebhookDialog
        open={showCreateDialog}
        onOpenChange={handleCloseCreateDialog}
        webhook={editingWebhook}
        onSuccess={() => {
          queryClient.invalidateQueries(["webhooks"]);
          handleCloseCreateDialog();
        }}
      />

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("webhooks.deleteWebhook")}</DialogTitle>
            <DialogDescription>
              {t(
                "webhooks.deleteConfirm",
                'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
                { name: selectedWebhook?.name || selectedWebhook?.url },
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate(selectedWebhook?.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending
                ? t("common.deleting")
                : t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Webhook Create/Edit Dialog Component
const WebhookDialog = ({ open, onOpenChange, webhook, onSuccess }) => {
  const { t } = useTranslation();
  const WEBHOOK_EVENTS = useWebhookEvents();
  const [formData, setFormData] = useState({
    name: "",
    url: "",
    events: [],
    secret: "",
    secretGraceHours: 24,
    isActive: true,
  });
  const [errors, setErrors] = useState({});

  // Reset form when dialog opens/closes or webhook changes. The secret is
  // never returned by the API, so in edit mode the field starts empty and
  // filling it means "rotate to this new secret".
  useEffect(() => {
    if (open) {
      setFormData({
        name: webhook?.name || "",
        url: webhook?.url || "",
        events: webhook?.events || [],
        secret: "",
        secretGraceHours: 24,
        isActive: webhook?.isActive ?? true,
      });
      setErrors({});
    }
  }, [open, webhook]);

  const mutation = useMutation({
    mutationFn: (data) =>
      webhook ? webhooksApi.update(webhook.id, data) : webhooksApi.create(data),
    onSuccess: () => {
      toast.success(webhook ? t("webhooks.updated") : t("webhooks.created"));
      onSuccess();
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || t("errors.generic"));
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!formData.url) newErrors.url = t("webhooks.urlRequired");
    else if (!formData.url.startsWith("https://")) {
      newErrors.url = t("webhooks.urlHttps");
    }
    if (formData.events.length === 0) {
      newErrors.events = t("webhooks.eventsRequired");
    }

    if (formData.secret && formData.secret.length < 16) {
      newErrors.secret = t("webhooks.secretTooShort");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Omit the secret when blank (create: server generates one; update:
    // current secret is kept). The grace window only applies to rotation.
    const { secret, secretGraceHours, ...rest } = formData;
    const payload = { ...rest };
    if (secret) {
      payload.secret = secret;
      if (webhook) payload.secretGraceHours = secretGraceHours;
    }

    mutation.mutate(payload);
  };

  const toggleEvent = (event) => {
    if (formData.events.includes(event)) {
      setFormData({
        ...formData,
        events: formData.events.filter((e) => e !== event),
      });
    } else {
      setFormData({ ...formData, events: [...formData.events, event] });
    }
  };

  const selectAllEvents = () => {
    setFormData({ ...formData, events: WEBHOOK_EVENTS.map((e) => e.value) });
  };

  const clearAllEvents = () => {
    setFormData({ ...formData, events: [] });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {webhook ? t("webhooks.editWebhook") : t("webhooks.add")}
          </DialogTitle>
          <DialogDescription>{t("webhooks.dialogDesc")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">
              {t("webhooks.name")}{" "}
              <span className="text-muted-foreground">
                ({t("common.optional")})
              </span>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder={t("webhooks.namePlaceholder")}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="url">{t("webhooks.endpointUrl")}</Label>
            <Input
              id="url"
              type="url"
              value={formData.url}
              onChange={(e) =>
                setFormData({ ...formData, url: e.target.value })
              }
              placeholder="https://your-server.com/webhook"
              className={errors.url ? "border-destructive" : ""}
            />
            {errors.url && (
              <p className="text-sm text-destructive">{errors.url}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="secret">
              {webhook
                ? t("webhooks.rotateSecret")
                : t("webhooks.signingSecret")}{" "}
              <span className="text-muted-foreground">
                ({t("common.optional")})
              </span>
            </Label>
            <Input
              id="secret"
              value={formData.secret}
              onChange={(e) =>
                setFormData({ ...formData, secret: e.target.value })
              }
              placeholder="whsec_..."
              className={multipleClassesName(
                "font-mono text-sm",
                errors.secret ? "border-destructive" : "",
              )}
            />
            {errors.secret && (
              <p className="text-sm text-destructive">{errors.secret}</p>
            )}
            <p className="text-xs text-muted-foreground">
              {webhook
                ? t("webhooks.rotateSecretHelp")
                : t("webhooks.secretHelp")}
            </p>
          </div>

          {webhook && formData.secret && (
            <div className="space-y-2">
              <Label htmlFor="secretGraceHours">
                {t("webhooks.graceWindow")}
              </Label>
              <Select
                value={String(formData.secretGraceHours)}
                onValueChange={(value) =>
                  setFormData({ ...formData, secretGraceHours: Number(value) })
                }
              >
                <SelectTrigger id="secretGraceHours">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">
                    {t("webhooks.graceImmediate")}
                  </SelectItem>
                  <SelectItem value="24">{t("webhooks.grace24")}</SelectItem>
                  <SelectItem value="48">{t("webhooks.grace48")}</SelectItem>
                  <SelectItem value="72">{t("webhooks.grace72")}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t("webhooks.graceWindowHelp")}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{t("webhooks.events.title")}</Label>
              <div className="space-x-2">
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs"
                  onClick={selectAllEvents}
                >
                  {t("common.selectAll")}
                </Button>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs"
                  onClick={clearAllEvents}
                >
                  {t("common.clearAll")}
                </Button>
              </div>
            </div>
            {errors.events && (
              <p className="text-sm text-destructive">{errors.events}</p>
            )}
            <div className="space-y-1 max-h-48 overflow-y-auto border rounded-lg p-2">
              {WEBHOOK_EVENTS.map((event) => (
                <label
                  key={event.value}
                  htmlFor="eventValue"
                  className="flex items-start gap-3 p-2 rounded hover:bg-muted cursor-pointer"
                >
                  <Checkbox
                    checked={formData.events.includes(event.value)}
                    onCheckedChange={() => toggleEvent(event.value)}
                    className="mt-0.5"
                  />
                  <div className="space-y-0.5">
                    <p className="text-sm font-medium leading-none">
                      {event.label}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {event.desc}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="isActive">{t("webhooks.active")}</Label>
              <p className="text-xs text-muted-foreground">
                {t("webhooks.activeHelp")}
              </p>
            </div>
            <Switch
              id="isActive"
              checked={formData.isActive}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, isActive: checked })
              }
            />
          </div>

          <DialogFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {multipleClassesName([
                mutation.isPending ? t("common.saving") : "",
                webhook ? t("common.saveChanges") : t("common.create"),
              ])}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Webhooks;
